import {
    ActionTypeEnum,
    EStatus,
    IJson,
    IJsonScenario,
    IPayloadScenario,
    IRootState,
    TFetchJsonContext,
    TInitWidgetState,
    TPage,
    TTriggerWidgetAction,
    TWAction,
    ValidationErrors,
} from '@libs/types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { CFAccessDeniedError, fetchJson, initWidgetState, isLoadingAction, saveDesignSystem, saveFeatureFlags } from '../../../foundation';
import { resolveScenarioTheme } from '../../service/ThemeService';
import { setStatus, setTheme } from '../scenario.slice';

/** Page-level fields of a response (the page without its `widgets`): read by the page-change hook, not kept in the state. */
export type TPageFields = Omit<TPage, 'widgets'> & Record<string, unknown>;

export type TScenarioPayload = IPayloadScenario & { pageFields?: TPageFields };

/** `updateScenario` argument: the response to render and, when known, the action that requested it. */
export type TUpdateScenarioArg = {
    json: IJson;
    action?: TWAction;
};

const getPageFields = (page: TPage | undefined): TPageFields | undefined => {
    if (!page) return undefined;
    const { widgets: _widgets, ...fields } = page;
    return fields;
};

const getPayload = (scenario: IJsonScenario): TScenarioPayload => {
    const { mutableWidgetsMap, bottomSheetMap, modalWindowMap, internalIdWidgets, systemActions, submitWidget, rootWidgets } = initWidgetState(
        scenario.pages,
    ) as TInitWidgetState;
    return {
        scenarioId: scenario?.scenarioId,
        currentPageId: scenario?.pages?.[0]?.id,
        pageFields: getPageFields(scenario?.pages?.[0]),
        configuration: scenario?.configuration ?? {},
        bottomSheetMap,
        mutableWidgetsMap,
        submitWidget,
        internalIdWidgets,
        modalWindowMap,
        systemActions,
        rootWidgets,
    };
};

const getFetchJsonContext = (scenario: IRootState['scenario'], dispatch: (action: unknown) => void): TFetchJsonContext => ({
    currentPageId: scenario.currentPageId,
    mutableWidgetsMap: scenario.mutableWidgetsMap,
    setPlayerState: (status: EStatus) => {
        dispatch(setStatus(status));
    },
});

/** Applies the response-level configuration that lives outside the scenario slice: design system, feature flags and theme. */
const applyScenarioConfiguration = async (scenario: IJsonScenario, dispatch: (action: unknown) => void) => {
    saveDesignSystem(scenario?.configuration?.designSystem);
    saveFeatureFlags(scenario?.configuration?.featureFlags);
    const theme = await resolveScenarioTheme(scenario);
    if (theme) dispatch(setTheme(theme));
};

export const fetchScenario = createAsyncThunk<
    TScenarioPayload | undefined,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchScenario', async ({ action, widgetUid, isShowLoader, scenarioId, currentPageId, ...options }, thunkAPI) => {
    try {
        const context = getFetchJsonContext(thunkAPI.getState().scenario, thunkAPI.dispatch);
        let json = await fetchJson(action, context, { ...options, widgetUid, isShowLoader: isShowLoader || isLoadingAction(action) });

        const isDelayedProductAction =
            (action?.type === ActionTypeEnum.Product || action?.type === ActionTypeEnum.ProductJump) && action?.delay && action.delay > 0;

        if (!isDelayedProductAction) {
            await applyScenarioConfiguration(json.scenario, thunkAPI.dispatch);
            return getPayload(json.scenario);
        }

        const isSamePage = json.scenario.scenarioId === scenarioId && json.scenario.pages[0]?.id === currentPageId;

        if (isSamePage) {
            json = await fetchJson(undefined, context, { ...options, widgetUid, isShowLoader: true });
        }
        await applyScenarioConfiguration(json.scenario, thunkAPI.dispatch);
        return getPayload(json.scenario);
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(`fetchScenario: ${widgetUid ?? ''}; ${action?.name ?? ''}  ${e}`);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('fetchScenario: request was aborted by core');
        } else if (isCFAccessDeniedError) {
            console.log('fetchScenario: CFAccessDeniedError');
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            // Keep the action (and the widget it came from) in the error state so the UI can
            // replay the exact request — see the offline popup retry in the consumer WidgetWrapper
            // and the captcha replay in ChallengeIframe. `isOffline` is captured at failure time so
            // the UI can tell a lost connection from a real backend error even after the network is back.
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
                widgetUid,
                actionForRepeat: action,
                isOffline: !navigator.onLine,
            });
        }
    }
});

export const fetchJsonAsync = createAsyncThunk<
    IJson,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchJsonAsync', async ({ action, widgetUid, isShowLoader, scenarioId: _scenarioId, currentPageId: _currentPageId, ...options }, thunkAPI) => {
    try {
        const context = getFetchJsonContext(thunkAPI.getState().scenario, thunkAPI.dispatch);
        return await fetchJson(action, context, { ...options, widgetUid, isShowLoader: isShowLoader ?? isLoadingAction(action) });
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(`fetchJsonAsync: ${widgetUid ?? ''}; ${action?.name ?? ''} ${e}`);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('fetchJsonAsync: request was aborted by core');
            throw e;
        } else if (isCFAccessDeniedError) {
            console.log('fetchJsonAsync: CFAccessDeniedError');
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
                widgetUid,
                actionForRepeat: action,
                isOffline: !navigator.onLine,
            });
        }
    }
});

export const updateScenario = createAsyncThunk<
    TScenarioPayload | undefined,
    TUpdateScenarioArg,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('updateScenario', async ({ json }, thunkAPI) => {
    await applyScenarioConfiguration(json.scenario, thunkAPI.dispatch);
    return getPayload(json.scenario);
});
